import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent
} from "@dnd-kit/core"
import { SortableContext, rectSortingStrategy, useSortable } from "@dnd-kit/sortable"
import {
  DownloadOutlined,
  FullscreenOutlined,
  ReloadOutlined
} from "@ant-design/icons"
import { CSS } from "@dnd-kit/utilities"
import {
  Alert,
  Button,
  Card,
  Col,
  Flex,
  Result,
  Row,
  Skeleton,
  Space,
  Typography,
  message
} from "antd"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"

import { neutral, shadows } from "./dashboardTokens"

import { ActionTooltip } from "../../../../components/ActionTooltip.tsx"
import { WidgetExpandModal } from "../../../../components/widgets/WidgetExpandModal"
import { WidgetRenderer } from "../../../../components/widgets/WidgetRenderer"
import type {
  Capability,
  WidgetInstance,
  WidgetPayload,
  WidgetProps,
  WidgetType
} from "../../../../components/widgets/types"
import { exportWidgetCsv } from "../../../../components/widgets/widgetCsv"
import { getUIPreferences } from "../../../User/services"
import { useAppDispatch } from "../../../../hooks/use-app-dispatch"
import { useAppSelector } from "../../../../hooks/use-app-selector"
import { useDebouncedCallback } from "../../../../hooks/use-debounced-callback"
import { useProductTour } from "../../../Guide/tour/ProductTourContext.tsx"
import { useGlobalFilterOptions } from "../../hooks/useGlobalFilterOptions"
import { resolveDashboardWidget } from "../../api/services"
import {
  buildFilterParams,
  createEmptyFilters,
  hasActiveFilters,
  parseActiveViewGlobalFilters,
  parseGlobalFilters
} from "../../redux/globalFilters"
import {
  fetchNba,
  setConfigKey,
  setGlobalFilters,
  setTeamScope
} from "../../redux/portfolio.slice"
import {
  dropResolvedWidget,
  fetchCatalog,
  fetchWidgets,
  isWidgetSnapshotFresh,
  resolveWidget
} from "../../redux/widgets.slice"
import type { FilterLabels, GlobalFilters } from "../../redux/portfolio.types"
import {
  ENGAGEMENT_INSTANCE,
  ENGAGEMENT_METRIC,
  prefixFilterParams,
  useEngagementWidgetHost
} from "../../hooks/useEngagementWidgetHost"
import { AddTileDrawer } from "./AddTileDrawer"
import { ExportButtons } from "./ExportButtons"
import { FilterBar } from "./FilterBar"
import { RemoveTileBar } from "./RemoveTileBar"
import { KPI_CARD_COL } from "./kpiGrid"
import { PortfolioDashboard } from "./PortfolioDashboard"
import { feedWidget } from "./portfolioWidgetAdapter"
import { SavedViewBar } from "./SavedViewBar"
import { TimeWindowControl } from "./TimeWindowControl"
import { useDashboardLayout } from "./useDashboardLayout"
import {
  BUILT_IN_SLUGS,
  CASH_FLOW_SLUG,
  NBA_FEED_SLUG,
  PROJECT_STATUS_SLUG,
  type AdaptedWidget,
  adaptResolved,
  catalogBoardSpecs,
  catalogFeedAlertSpecs,
  catalogKpiSpecs,
  catalogSnapshotSpecs,
  currencyFromWidgets,
  fixedWidgetInstances,
  fixedWidgetInstancesFor,
  indexCatalog,
  isBoardSlug,
  boardChartRows,
  isFeedAlertSlug,
  isKpiSlug,
  isSnapshotSlug,
  liftTileToInstance,
  metricFromSlug,
  resolvedSlug,
  responseToInstances,
  slugFor,
  specToInstance,
  typeFromSlug,
  valueKey,
} from "./widgetsResponseAdapter"

const NBA_POLL_INTERVAL = 5 * 60 * 1000
const CATALOG_MAX_RETRIES = 3
const CATALOG_RETRY_BASE_MS = 300

const CapabilityActions = ({
  capabilities,
  onRefresh,
  onExpand,
  onExport,
}: {
  capabilities?: Capability[]
  onRefresh: () => void
  onExpand?: () => void
  onExport?: () => void
}) => {
  const { t } = useTranslation()
  const caps = capabilities ?? []
  const showRefresh = caps.includes("refresh")
  const showExpand = caps.includes("expand") && Boolean(onExpand)
  const showExport = caps.includes("export") && Boolean(onExport)
  if (!showRefresh && !showExpand && !showExport) return null
  return (
    <Space size={4}>
      {showRefresh && (
        <ActionTooltip title={t("widget.action.refresh", "Refresh")}>
          <Button
            type="text"
            size="small"
            aria-label={t("widget.action.refresh", "Refresh")}
            data-testid="widget-refresh"
            icon={<ReloadOutlined />}
            onClick={onRefresh}
          />
        </ActionTooltip>
      )}
      {showExpand && (
        <ActionTooltip title={t("widget.action.expand", "Expand")}>
          <Button
            type="text"
            size="small"
            aria-label={t("widget.action.expand", "Expand")}
            data-testid="widget-expand"
            icon={<FullscreenOutlined />}
            onClick={onExpand}
          />
        </ActionTooltip>
      )}
      {showExport && (
        <ActionTooltip title={t("widget.action.export", "Export")}>
          <Button
            type="text"
            size="small"
            aria-label={t("widget.action.export", "Export")}
            data-testid="widget-export"
            icon={<DownloadOutlined />}
            onClick={onExport}
          />
        </ActionTooltip>
      )}
    </Space>
  )
}

interface KpiCardShellProps {
  slug: string
  adapted?: AdaptedWidget
  currency?: string
  onHide?: (slug: string) => void
}

const KpiCardBody = ({ adapted, currency }: { adapted?: AdaptedWidget; currency?: string }) => {
  if (!adapted) {
    return (
      <WidgetRenderer
        widget={{ id: "pending", type: "kpi" } as WidgetInstance}
        payload={null}
        state="loading"
      />
    )
  }
  return (
    <WidgetRenderer
      widget={adapted.widget}
      payload={adapted.payload as WidgetPayload}
      state="ready"
      currency={currency}
    />
  )
}

const SortableKpiCard = ({ slug, adapted, currency, onHide }: KpiCardShellProps) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: slug })
  const style = { transform: CSS.Transform.toString(transform), transition }
  return (
    <Col {...KPI_CARD_COL} ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {onHide && (
        <RemoveTileBar title={adapted?.widget.title} onRemove={() => onHide(slug)} />
      )}
      <KpiCardBody adapted={adapted} currency={currency} />
    </Col>
  )
}

const StaticKpiCard = ({ slug, adapted, currency }: KpiCardShellProps) => (
  <Col {...KPI_CARD_COL} key={slug}>
    <KpiCardBody adapted={adapted} currency={currency} />
  </Col>
)

interface BoardWidgetCardProps {
  slug: string
  adapted?: AdaptedWidget
  currency?: string
  isEditing?: boolean
  refreshing?: boolean
  onHide?: (slug: string) => void
  onRefresh?: () => void
  onExpand?: () => void
  onEngagementDrill?: WidgetProps["onEngagementDrill"]
  loadEngagementRange?: WidgetProps["loadEngagementRange"]
}

const BoardWidgetCard = ({
  slug,
  adapted,
  currency,
  onEngagementDrill,
  loadEngagementRange,
  isEditing,
  refreshing,
  onHide,
  onRefresh,
  onExpand,
}: BoardWidgetCardProps) => {
  const body = adapted ? (
    <WidgetRenderer
      widget={adapted.widget}
      payload={adapted.payload as WidgetPayload}
      state={refreshing ? "loading" : "ready"}
      currency={currency}
      onRefresh={onRefresh}
      onExpand={onExpand}
      onEngagementDrill={onEngagementDrill}
      loadEngagementRange={loadEngagementRange}
    />
  ) : (
    <WidgetRenderer
      widget={{ id: slug, type: typeFromSlug(slug) as WidgetType } as WidgetInstance}
      payload={null}
      state="loading"
    />
  )
  // Tables and lists draw their own header; a chart on the board needs the
  // same titled card the fixed Cash Flow chart sits in.
  const framed =
    typeFromSlug(slug) === "chart" ? (
      <Card
        title={adapted?.widget.title}
        size="small"
        extra={
          onRefresh ? (
            <CapabilityActions
              capabilities={adapted?.widget.capabilities}
              onRefresh={onRefresh}
              onExpand={onExpand}
              onExport={
                // The table exports its own rows, in the grouping on screen.
                adapted?.payload.chart?.engagement
                  ? undefined
                  : () =>
                      exportWidgetCsv({
                        title: adapted?.widget.title ?? slug,
                        payload: adapted?.payload
                      })
              }
            />
          ) : undefined
        }
      >
        {body}
      </Card>
    ) : (
      body
    )
  return (
    <Col xs={24} key={slug}>
      {isEditing && onHide && (
        <RemoveTileBar title={adapted?.widget.title} onRemove={() => onHide(slug)} />
      )}
      {framed}
    </Col>
  )
}

interface WidgetsDashboardProps {
  // Which `UIPreferences` key this instance's layout persists under — see
  // `useDashboardLayout`'s own doc comment. Omitted (the single-branch
  // Home's own default) means the dedicated `dashboard_config` field.
  preferenceKey?: string
  // Company HQ's own page-level branch filter — a fixed list of team ids
  // to narrow every tile by, or `undefined`/empty for "all accessible
  // branches". Never used outside Company HQ.
  teamScope?: string[]
}

export const WidgetsDashboard = ({ preferenceKey, teamScope }: WidgetsDashboardProps = {}) => {
  const { t, i18n } = useTranslation()
  const dispatch = useAppDispatch()
  const { window, globalFilters, nba } = useAppSelector((s) => s.portfolio)
  const engagementHost = useEngagementWidgetHost()
  // Bumped by the table card's Refresh: a new loader re-reads every span.
  const [engagementGeneration, setEngagementGeneration] = useState(0)
  // Keyed on the filters' values: restoring the same filters must not re-read
  // the table's spans.
  const engagementFilterParams = JSON.stringify(buildFilterParams(globalFilters ?? undefined))
  const engagementHandlers = useMemo(
    () => {
      const params = JSON.parse(engagementFilterParams) as Record<string, string>
      return engagementHost.bind({
        resolve: (span) =>
          resolveDashboardWidget(ENGAGEMENT_INSTANCE, span, globalFilters ?? undefined),
        drillFilters: prefixFilterParams("dash_", params)
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [engagementHost.bind, engagementFilterParams, engagementGeneration]
  )
  const {
    status,
    fellBack,
    widgets,
    resolvedByKey,
    pendingResolveIds,
    generatedAt,
    catalog,
    catalogStatus
  } = useAppSelector((s) => s.widgets)
  const financePermissions = useAppSelector(
    (s) => s.auth?.permissions?.codes?.projects?.finances
  )

  const windowRef = useRef(window)
  windowRef.current = window
  const didInit = useRef(false)
  const inited = useRef(false)
  const pendingConfig = useRef<{ has: boolean; raw: unknown }>({ has: false, raw: null })
  const [prefsTick, setPrefsTick] = useState(0)
  const [expandedSlug, setExpandedSlug] = useState<string | null>(null)
  const [selectedWindowPreset, setSelectedWindowPreset] = useState(window.preset)
  const [addTileOpen, setAddTileOpen] = useState(false)

  useEffect(() => {
    setSelectedWindowPreset(window.preset)
  }, [window.preset])

  const specIndex = useMemo(() => indexCatalog(catalog), [catalog])

  // Absent permissions mean "not loaded yet" — keep the slot rather than
  // flicker it away for everyone.
  const fixedInstances = useMemo(
    () =>
      fixedWidgetInstancesFor(
        !financePermissions ||
          Boolean(
            financePermissions.project_finance_view ||
              financePermissions.project_finance_full_access
          )
      ),
    [financePermissions]
  )

  const layout = useDashboardLayout({
    version: 4,
    resolveInstance: (tileId: string) => liftTileToInstance(tileId, specIndex),
    fixedInstances,
    preferenceKey,
  })

  // Company HQ's page-level branch filter, mirrored into `state.portfolio`
  // before anything fetches — declared ahead of the init effect below so
  // the very first `fetchWidgets` call already reads the right scope
  // rather than firing once unscoped and once correctly. A later change
  // (the user picks a different set of branches) explicitly refetches;
  // the very first run does not, since the init effect's own fetch covers
  // it. Never written back into a saved view — see `setTeamScope`.
  const teamScopeKeyRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    const key = JSON.stringify([teamScope ?? [], preferenceKey])
    if (teamScopeKeyRef.current === key) return
    const isFirst = teamScopeKeyRef.current === undefined
    teamScopeKeyRef.current = key
    dispatch(setTeamScope(teamScope ?? []))
    dispatch(setConfigKey(preferenceKey))
    if (!isFirst) void dispatch(fetchWidgets(windowRef.current))
  }, [teamScope, preferenceKey, dispatch])

  const { options: filterOptions, loading: filterOptionsLoading } =
    useGlobalFilterOptions()

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  )

  useEffect(() => {
    if (!layout.isEditing) setAddTileOpen(false)
  }, [layout.isEditing])

  // The Product Tour's dashboard-customization steps drive real Edit Layout
  // state through these two query params — the same `?addMember=1`-style
  // convention the Setup Assistant's Team step already uses to open a real
  // UI state via the URL rather than the tour engine reaching into this
  // component directly. Nothing here ever persists: entering edit mode this
  // way is exactly `startEdit()`, the same pure-local-state call the real
  // "Edit Layout" button makes (omitting its explicit order seed falls back
  // to the layout's own current order internally), and the tour never calls
  // `save()`. Declared here, before any of this component's early returns
  // (loading/error/fellBack below), so these hooks always run in the same
  // order every render — moving them lower once broke exactly that.
  const [tourSearchParams, setTourSearchParams] = useSearchParams()
  const tourEdit = tourSearchParams.get("tourEdit") === "1"
  const tourAddTile = tourSearchParams.get("tourAddTile") === "1"
  const { isActive: productTourActive } = useProductTour()
  useEffect(() => {
    if (tourEdit && !layout.isEditing) layout.startEdit()
    else if (!tourEdit && layout.isEditing) layout.cancel()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourEdit])
  useEffect(() => {
    if (tourAddTile) setAddTileOpen(true)
  }, [tourAddTile])
  // `ProductTourContext.stop()` (Escape, or the overlay's Close/Skip) only
  // resets the tour's own state — it never touches the router. Left alone,
  // exiting early on a dashboard-editing step leaves `tourEdit`/`tourAddTile`
  // in the URL: the real dashboard stays silently in Edit Layout mode, and a
  // reload, Back, or a bookmark of this exact link re-triggers it again later
  // with zero tour activity. Stripping the params here lets the two effects
  // above run their normal cleanup (`layout.cancel()`, which in turn closes
  // the Add Tile drawer via the `!layout.isEditing` effect) exactly as if the
  // tour had moved on to its next step and navigated away cleanly.
  useEffect(() => {
    if (productTourActive || !(tourEdit || tourAddTile)) return
    setTourSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete("tourEdit")
        next.delete("tourAddTile")
        return next
      },
      { replace: true }
    )
  }, [productTourActive, tourEdit, tourAddTile, setTourSearchParams])

  useEffect(() => {
    if (!didInit.current) {
      didInit.current = true
      void dispatch(fetchCatalog())
      void dispatch(fetchWidgets(windowRef.current))
      void dispatch(fetchNba())
      void (async () => {
        try {
          const prefs = await getUIPreferences()
          const raw =
            prefs.ok && prefs.data
              ? preferenceKey && preferenceKey !== "dashboard_config"
                ? (prefs.data.preferences as Record<string, unknown> | undefined)?.[
                    preferenceKey
                  ] ?? null
                : prefs.data.dashboard_config
              : null
          pendingConfig.current = { has: true, raw }
        } catch {
          pendingConfig.current = { has: true, raw: null }
        }
        setPrefsTick((n) => n + 1)
      })()
    }

    if (fellBack) return

    const intervalId = setInterval(() => {
      void dispatch(fetchNba())
    }, NBA_POLL_INTERVAL)

    return () => clearInterval(intervalId)
  }, [dispatch, fellBack])

  // KPI titles come from the catalog; a failed fetch would otherwise leave raw
  // metric keys on screen for the whole session (the landing page mounts inside
  // the login/cold-boot request burst, where one-off failures are likely).
  const catalogRetries = useRef(0)
  useEffect(() => {
    if (fellBack || catalogStatus !== "error") return
    if (catalogRetries.current >= CATALOG_MAX_RETRIES) return
    const attempt = catalogRetries.current + 1
    const timer = setTimeout(() => {
      catalogRetries.current = attempt
      void dispatch(fetchCatalog())
    }, CATALOG_RETRY_BASE_MS * attempt)
    return () => clearTimeout(timer)
  }, [catalogStatus, fellBack, dispatch])

  // The v3→v4 lift resolves tile ids through the catalog, so hold init until the
  // catalog and the stored preferences are both in hand.
  useEffect(() => {
    if (inited.current || !catalog || !pendingConfig.current.has) return
    inited.current = true
    const raw = pendingConfig.current.raw
    layout.initFromConfig(raw)
    const restored = parseActiveViewGlobalFilters(raw)
    dispatch(setGlobalFilters(restored))
    if (hasActiveFilters(restored)) {
      void dispatch(fetchWidgets(windowRef.current))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, prefsTick])

  useEffect(() => {
    if (widgets.length) {
      layout.registerInstances(responseToInstances(widgets, specIndex))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgets, specIndex])

  const windowKeyRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (fellBack) return
    const key = JSON.stringify(window)
    if (windowKeyRef.current === undefined) {
      windowKeyRef.current = key
      return
    }
    if (windowKeyRef.current !== key) {
      windowKeyRef.current = key
      void dispatch(fetchWidgets(window))
    }
  }, [window, dispatch, fellBack])

  const applyFiltersDebounced = useDebouncedCallback(
    (next: GlobalFilters, nextLabels: FilterLabels, viewId: string | null) => {
      void dispatch(fetchWidgets(windowRef.current))
      void layout.persistGlobalFilters(next, nextLabels, viewId).catch(() => {
        void message.error(t("portfolio.filters.persistFailed", "Failed to save filters"))
      })
    },
    350
  )

  const currency = useMemo(() => currencyFromWidgets(widgets), [widgets])
  const hasFreshSnapshot = widgets.length > 0 && isWidgetSnapshotFresh(generatedAt)
  const generatedAtLabel = useMemo(() => {
    if (!generatedAt) return ""
    const parsed = new Date(generatedAt)
    if (Number.isNaN(parsed.getTime())) return ""
    return new Intl.DateTimeFormat(i18n.language?.startsWith("de") ? "de-DE" : "en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(parsed)
  }, [generatedAt, i18n.language])

  const adaptedByKey = useMemo(() => {
    const out: Record<string, AdaptedWidget> = {}
    for (const [key, raw] of Object.entries(resolvedByKey)) {
      out[key] = adaptResolved(raw, specIndex, currency)
    }
    return out
  }, [resolvedByKey, specIndex, currency])

  const kpiAdapted = (slug: string): AdaptedWidget | undefined =>
    adaptedByKey[valueKey("kpi", metricFromSlug(slug))]

  const responseKpiSlugs = useMemo(() => {
    const slugs: string[] = []
    for (const w of widgets) {
      if (w.type !== "kpi") continue
      const s = resolvedSlug(w, specIndex)
      if (s) slugs.push(s)
    }
    return slugs
  }, [widgets, specIndex])

  const responseBoardSlugs = useMemo(() => {
    const slugs: string[] = []
    for (const w of widgets) {
      const s = resolvedSlug(w, specIndex)
      if (s && isBoardSlug(s)) slugs.push(s)
    }
    return slugs
  }, [widgets, specIndex])

  const responseSnapshotSlugs = useMemo(() => {
    const slugs: string[] = []
    for (const w of widgets) {
      if (w.type !== "snapshot") continue
      const s = resolvedSlug(w, specIndex)
      if (s) slugs.push(s)
    }
    return slugs
  }, [widgets, specIndex])

  const responseSlugSet = useMemo(
    () => new Set([...responseKpiSlugs, ...responseBoardSlugs, ...responseSnapshotSlugs]),
    [responseKpiSlugs, responseBoardSlugs, responseSnapshotSlugs]
  )

  if (fellBack) return <PortfolioDashboard />

  if (status === "loading" || status === "idle") {
    return (
      <Flex vertical gap="middle">
        <Row gutter={[16, 16]}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Col key={i} {...KPI_CARD_COL}>
              <WidgetRenderer
                widget={{ id: `sk${i}`, type: "kpi" } as WidgetInstance}
                payload={null}
                state="loading"
              />
            </Col>
          ))}
        </Row>
      </Flex>
    )
  }

  if (status === "error" && !hasFreshSnapshot) {
    return (
      <Result
        status="error"
        title={t("portfolio.error.title")}
        extra={
          <Button onClick={() => void dispatch(fetchWidgets(window))}>
            {t("portfolio.error.retry")}
          </Button>
        }
      />
    )
  }

  const kpiOrder = (layout.order.length ? layout.order : responseKpiSlugs).filter(isKpiSlug)
  const shownKpiSlugs = kpiOrder.filter((slug) => !layout.hidden.includes(slug))
  const shownSet = new Set(shownKpiSlugs)
  const shownCount = shownKpiSlugs.filter((slug) => kpiAdapted(slug) !== undefined).length

  const boardBase = layout.order.length ? layout.order : responseBoardSlugs
  const boardOrder = boardBase
    .filter(isBoardSlug)
    .filter((slug) => !layout.hidden.includes(slug))
  // A chart placed on the board sits under Portfolio Overview in that card's
  // own grid column, so the two always share one width whatever the layout.
  const boardChartOrder = boardOrder.filter((slug) => typeFromSlug(slug) === "chart")
  // The Location tile reuses that exact same column width — explicit product
  // direction, so it reads as one deliberate section rather than a
  // full-bleed table/list row. `BoardWidgetCard` itself always renders
  // `<Col xs={24}>` (100% of its own containing `<Row>`), so nesting it one
  // level inside `dashboardRowGrid`'s left (68fr) cell — rather than adding a
  // width override prop — is enough: antd's grid is percentage-based against
  // its nearest ancestor, not the viewport.
  const mapOrder = boardOrder.filter((slug) => typeFromSlug(slug) === "map")
  const boardListOrder = boardOrder.filter(
    (slug) => typeFromSlug(slug) !== "chart" && typeFromSlug(slug) !== "map"
  )
  const boardAdapted = (slug: string): AdaptedWidget | undefined =>
    adaptedByKey[valueKey(typeFromSlug(slug), metricFromSlug(slug))]

  const feedAlertOrder = layout.order
    .filter(isFeedAlertSlug)
    .filter((slug) => !layout.hidden.includes(slug))

  const snapshotBase = layout.order.length ? layout.order : responseSnapshotSlugs
  const snapshotOrder = snapshotBase
    .filter(isSnapshotSlug)
    .filter((slug) => !layout.hidden.includes(slug))

  // Seeding the order from one section would drop every other section's widgets
  // from the layout the moment Edit Layout is opened, and saving would persist
  // the loss — the snapshot card vanished until Restore Default.
  const editableOrder = [...kpiOrder, ...snapshotOrder, ...boardOrder, ...feedAlertOrder]

  // Add Tile offers whatever is not on screen: a removed tile comes back from
  // there, not only one that was never added.
  const boardShown = new Set(boardOrder)
  const feedAlertShown = new Set(feedAlertOrder)
  const snapshotShown = new Set(snapshotOrder)
  const cashFlowShown = !layout.hidden.includes(CASH_FLOW_SLUG)
  const projectStatusShown = !layout.hidden.includes(PROJECT_STATUS_SLUG)
  const nbaShown = !layout.hidden.includes(NBA_FEED_SLUG)
  const builtInEntries = [
    ...(fixedInstances.some((instance) => instance.slug === CASH_FLOW_SLUG)
      ? [{ id: CASH_FLOW_SLUG, title: t("portfolio.cashFlow.title") }]
      : []),
    { id: PROJECT_STATUS_SLUG, title: t("portfolio.status.title") },
    { id: NBA_FEED_SLUG, title: t("portfolio.nba.title", "Next Best Actions") },
  ]

  const catalogEntries = [
    ...catalogKpiSpecs(catalog)
      .map((spec) => ({
        id: slugFor(spec.data_source, spec.metric, spec.type),
        title: spec.title,
      }))
      .filter((entry) => !shownSet.has(entry.id)),
    ...catalogBoardSpecs(catalog)
      .map((spec) => ({
        id: slugFor(spec.data_source, spec.metric, spec.type),
        title: spec.title,
      }))
      .filter((entry) => !boardShown.has(entry.id)),
    ...catalogFeedAlertSpecs(catalog)
      .map((spec) => ({
        id: slugFor(spec.data_source, spec.metric, spec.type),
        title: spec.title,
      }))
      .filter((entry) => !feedAlertShown.has(entry.id)),
    ...catalogSnapshotSpecs(catalog)
      .map((spec) => ({
        id: slugFor(spec.data_source, spec.metric, spec.type),
        title: spec.title,
      }))
      .filter((entry) => !snapshotShown.has(entry.id)),
    ...builtInEntries.filter((entry) => layout.hidden.includes(entry.id)),
  ]

  const cashFlowAdapted = adaptedByKey[valueKey("chart", "cash_flow_series")]
  const donutAdapted = adaptedByKey[valueKey("chart", "project_status_breakdown")]

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      layout.reorder(String(active.id), String(over.id))
    }
  }

  const handleSave = async () => {
    try {
      await layout.save()
      void dispatch(fetchWidgets(windowRef.current))
    } catch {
      void message.error(t("portfolio.editLayout.saveFailed"))
    }
  }

  const handleFiltersChange = (next: GlobalFilters, nextLabels: FilterLabels) => {
    dispatch(setGlobalFilters(next))
    applyFiltersDebounced(next, nextLabels, layout.activeViewId)
  }

  const handleSwitch = async (id: string) => {
    try {
      const target = layout.views.find((v) => v.id === id)
      await layout.switchView(id)
      dispatch(setGlobalFilters(parseGlobalFilters(target)))
      void dispatch(fetchWidgets(windowRef.current))
    } catch {
      void message.error(t("portfolio.editLayout.switchFailed", "Failed to switch view"))
    }
  }

  const handleCreate = async (name: string, from: "current" | "default") => {
    try {
      const source =
        from === "default"
          ? layout.views.find((view) => view.id === layout.defaultViewId)
          : layout.views.find((view) => view.id === layout.activeViewId)
      await layout.createView(name, from)
      dispatch(setGlobalFilters(parseGlobalFilters(source)))
      void dispatch(fetchWidgets(windowRef.current))
    } catch {
      void message.error(t("portfolio.editLayout.createFailed", "Failed to create view"))
    }
  }

  const handleRename = async (id: string, name: string) => {
    try {
      await layout.renameView(id, name)
    } catch {
      void message.error(t("portfolio.editLayout.renameFailed", "Failed to rename view"))
    }
  }

  const handleDelete = async (id: string) => {
    try {
      const fallback =
        id === layout.activeViewId
          ? layout.views.find((view) => view.id !== id)
          : undefined
      await layout.deleteView(id)
      if (fallback) {
        dispatch(setGlobalFilters(parseGlobalFilters(fallback)))
        void dispatch(fetchWidgets(windowRef.current))
      }
    } catch {
      void message.error(t("portfolio.editLayout.deleteFailed", "Failed to delete view"))
    }
  }

  const handleSetDefault = async (id: string) => {
    try {
      await layout.setDefault(id)
    } catch {
      void message.error(t("portfolio.editLayout.setDefaultFailed", "Failed to set default"))
    }
  }

  const handleRestoreDefault = async () => {
    try {
      await layout.restoreDefault()
      dispatch(setGlobalFilters(createEmptyFilters()))
      void dispatch(fetchWidgets(windowRef.current))
    } catch {
      void message.error(t("portfolio.editLayout.restoreFailed"))
    }
  }

  const handleAdd = (slug: string) => {
    if (BUILT_IN_SLUGS.includes(slug)) {
      if (layout.hidden.includes(slug)) layout.toggleHidden(slug)
      return
    }
    const spec = specIndex.bySlug.get(slug)
    if (!spec) return
    const instance = specToInstance(spec)
    layout.addWidget(instance)
    void dispatch(resolveWidget({ instance, window: windowRef.current }))
  }

  const handleHide = (slug: string) => {
    layout.toggleHidden(slug)
    if (!BUILT_IN_SLUGS.includes(slug) && !responseSlugSet.has(slug)) {
      dispatch(dropResolvedWidget(valueKey(typeFromSlug(slug), metricFromSlug(slug))))
    }
  }

  const refetch = () => void dispatch(fetchWidgets(windowRef.current))

  const resolveKeyOf = (slug: string) =>
    valueKey(typeFromSlug(slug), metricFromSlug(slug))

  const isRefreshing = (slug: string) =>
    Boolean(pendingResolveIds[resolveKeyOf(slug)])

  const handleWidgetRefresh = (slug: string) => {
    if (metricFromSlug(slug) === ENGAGEMENT_METRIC) setEngagementGeneration((value) => value + 1)
    const fixed = fixedWidgetInstances().find((i) => i.slug === slug)
    const spec = specIndex.bySlug.get(slug)
    const instance = fixed ?? (spec ? specToInstance(spec) : null)
    if (!instance) {
      refetch()
      return
    }
    void dispatch(resolveWidget({ instance, window: windowRef.current }))
  }

  const expandedAdapted = expandedSlug
    ? adaptedByKey[resolveKeyOf(expandedSlug)]
    : undefined

  const cardStyle: React.CSSProperties = {
    border: `1px solid ${neutral.border}`,
    borderRadius: 12,
    boxShadow: shadows.card,
  }

  // Financial Snapshot and Portfolio overview keep their own natural height —
  // that height is what defines each grid row. Cash Flow and Project Status
  // sit in the shorter/follower column and need to visually reach the same
  // top+bottom edges, which is the one place a card's outer box legitimately
  // needs `height: 100%` (the grid stretches the *cell*, but nothing makes
  // the *card inside it* look that tall unless the card says so too).
  //
  // The card must also be `display: flex; flex-direction: column` for that
  // to do anything — without it, `height: 100%` sizes the card correctly but
  // its body still sizes to its own content, so nothing downstream that
  // depends on a definite pixel height (recharts' `ResponsiveContainer
  // height="100%"`, in particular) has one to resolve against and silently
  // renders at zero height. That gap — Card said 100% tall, body never
  // participated in flex, so recharts collapsed to nothing — is what made
  // the Cash Flow chart disappear on the first pass of this fix.
  const stretchCardStyle: React.CSSProperties = {
    ...cardStyle,
    height: "100%",
    display: "flex",
    flexDirection: "column",
  }
  const stretchCardBodyBase: React.CSSProperties = {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  }
  // Cash Flow: the chart should actually grow into the extra height (a
  // taller time-series chart is more useful, not "empty") — content stretches.
  const fillCardStyles = { body: stretchCardBodyBase }
  const fillContentStyle: React.CSSProperties = { flex: 1, minHeight: 180 }
  // Project Status: stretching the donut ring itself to match a much taller
  // Portfolio overview would blow it up into an oversized ring, which reads
  // as worse, not better. Keep the donut+legend at their natural size and
  // centre that block in the extra height instead, so leftover space becomes
  // even padding rather than a dead gap under the legend.
  const centerCardStyles = { body: { ...stretchCardBodyBase, justifyContent: "center" } }

  // Both major rows share this exact template so the two columns line up
  // between rows (Financial Snapshot's left edge = Portfolio overview's left
  // edge, Cash Flow's right edge = Project Status's right edge) — the grid,
  // not the individual cards, owns the widths.
  const dashboardRowGrid =
    "grid grid-cols-1 xl:grid-cols-[minmax(0,68fr)_minmax(280px,32fr)] gap-4 items-stretch"

  return (
    <Flex vertical gap="middle" data-guide-target="home-dashboard-panel">
      {status === "error" && hasFreshSnapshot && (
        <Alert
          showIcon
          type="warning"
          message={t("portfolio.error.savedSnapshot", { time: generatedAtLabel })}
          action={
            <Button size="small" onClick={() => void dispatch(fetchWidgets(window))}>
              {t("portfolio.error.retry")}
            </Button>
          }
        />
      )}
      <Flex justify="space-between" align="center" wrap="wrap" gap="small">
        <SavedViewBar
          views={layout.views}
          activeViewId={layout.activeViewId}
          activeView={layout.activeView}
          defaultViewId={layout.defaultViewId}
          isEditing={layout.isEditing}
          startEdit={() => layout.startEdit(editableOrder)}
          cancel={layout.cancel}
          save={handleSave}
          restoreDefault={handleRestoreDefault}
          onAddTile={() => setAddTileOpen(true)}
          onSwitch={handleSwitch}
          onCreate={handleCreate}
          onRename={handleRename}
          onDelete={handleDelete}
          onSetDefault={handleSetDefault}
        />
        <ExportButtons
          window={window}
          filters={globalFilters ?? undefined}
          selectedPreset={selectedWindowPreset}
          teamScope={teamScope}
        />
      </Flex>

      <FilterBar
        filters={globalFilters ?? parseGlobalFilters(null)}
        options={filterOptions}
        filterLabels={layout.filterLabels}
        dateControl={
          <div data-testid="dashboard-date-filter">
            <TimeWindowControl
              selectedPreset={selectedWindowPreset}
              onSelectedPresetChange={setSelectedWindowPreset}
            />
          </div>
        }
        loading={filterOptionsLoading}
        onChange={handleFiltersChange}
      />

      <div
        className={
          snapshotOrder.length > 0 && cashFlowShown ? dashboardRowGrid : "grid grid-cols-1"
        }
      >
        {snapshotOrder.length > 0 && (
          <div style={{ minWidth: 0 }} data-guide-target="home-financial-snapshot-panel">
            <Row gutter={[16, 16]}>
              {snapshotOrder.map((slug) => (
                <BoardWidgetCard
                  key={slug}
                  slug={slug}
                  adapted={boardAdapted(slug)}
                  currency={currency}
                  isEditing={layout.isEditing}
                  refreshing={isRefreshing(slug)}
                  onHide={handleHide}
                  onRefresh={() => handleWidgetRefresh(slug)}
                />
              ))}
            </Row>
          </div>
        )}
        {cashFlowShown && (
          <div style={{ minWidth: 0 }}>
            {layout.isEditing && (
              <RemoveTileBar
                title={t("portfolio.cashFlow.title")}
                onRemove={() => handleHide(CASH_FLOW_SLUG)}
              />
            )}
            <Card
              title={t("portfolio.cashFlow.title")}
              size="small"
              style={stretchCardStyle}
              styles={fillCardStyles}
              extra={
                <CapabilityActions
                  capabilities={cashFlowAdapted?.widget.capabilities}
                  onRefresh={() => handleWidgetRefresh(CASH_FLOW_SLUG)}
                  onExpand={() => setExpandedSlug(CASH_FLOW_SLUG)}
                  onExport={() =>
                    exportWidgetCsv({
                      title: t("portfolio.cashFlow.title"),
                      payload: cashFlowAdapted?.payload,
                    })
                  }
                />
              }
            >
              {isRefreshing(CASH_FLOW_SLUG) ? (
                <Skeleton active paragraph={{ rows: 3 }} />
              ) : (
                <div style={fillContentStyle}>
                  <WidgetRenderer
                    widget={
                      cashFlowAdapted?.widget ??
                      ({ id: CASH_FLOW_SLUG, type: "chart" } as WidgetInstance)
                    }
                    payload={
                      cashFlowAdapted?.payload ??
                      { chart: { visualisation: "composed", cashFlow: { series: [] } } }
                    }
                    state="ready"
                    fillHeight
                  />
                </div>
              )}
            </Card>
          </div>
        )}
      </div>

      {/* Portfolio overview and Project Status share a row — a compact card
          beside the KPI grid reads as one deliberate section rather than two
          independently-sized full-width rows with a mostly-empty donut row
          underneath (2026-09-02 dashboard proportions pass). */}
      <div className={projectStatusShown ? dashboardRowGrid : "grid grid-cols-1"}>
        <div
          style={{ minWidth: 0 }}
          data-guide-target="dashboard-rearrange-panel"
          data-dashboard-row-cell
        >
          <Card
            size="small"
            style={cardStyle}
            title={
              <Flex align="baseline" gap={8}>
                <span style={{ fontSize: 14, fontWeight: 700, color: neutral.ink }}>
                  {t("portfolio.section.overview", "Portfolio overview")}
                </span>
                <span style={{ fontSize: 12, fontWeight: 400, color: neutral.faint }}>
                  {t("portfolio.section.metrics", "{{count}} metrics", { count: shownCount })}
                </span>
              </Flex>
            }
          >
            {layout.isEditing ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={shownKpiSlugs} strategy={rectSortingStrategy}>
                  <Row gutter={[16, 16]}>
                    {shownKpiSlugs.map((slug) => (
                      <SortableKpiCard
                        key={slug}
                        slug={slug}
                        adapted={kpiAdapted(slug)}
                        currency={currency}
                        onHide={handleHide}
                      />
                    ))}
                  </Row>
                </SortableContext>
              </DndContext>
            ) : (
              <Row gutter={[16, 16]}>
                {shownKpiSlugs.map((slug) => (
                  <StaticKpiCard
                    key={slug}
                    slug={slug}
                    adapted={kpiAdapted(slug)}
                    currency={currency}
                  />
                ))}
              </Row>
            )}
          </Card>
        </div>
        {projectStatusShown && (
          <div style={{ minWidth: 0 }}>
            {layout.isEditing && (
              <RemoveTileBar
                title={t("portfolio.status.title")}
                onRemove={() => handleHide(PROJECT_STATUS_SLUG)}
              />
            )}
            <Card
              title={t("portfolio.status.title")}
              size="small"
              style={stretchCardStyle}
              styles={centerCardStyles}
              extra={
                <CapabilityActions
                  capabilities={donutAdapted?.widget.capabilities}
                  onRefresh={() => handleWidgetRefresh(PROJECT_STATUS_SLUG)}
                  onExpand={() => setExpandedSlug(PROJECT_STATUS_SLUG)}
                  onExport={() =>
                    exportWidgetCsv({
                      title: t("portfolio.status.title"),
                      payload: donutAdapted?.payload,
                    })
                  }
                />
              }
            >
              {isRefreshing(PROJECT_STATUS_SLUG) ? (
                <Skeleton active paragraph={{ rows: 3 }} />
              ) : (
                // No `fillHeight` here on purpose: stretching the donut ring
                // itself to match a much taller Portfolio overview would blow
                // it up into an oversized ring. It stays its normal size and
                // the flex body (`centerCardStyles`, `justifyContent: center`)
                // centres that block in the extra height instead.
                <WidgetRenderer
                  widget={
                    donutAdapted?.widget ??
                    ({ id: PROJECT_STATUS_SLUG, type: "chart" } as WidgetInstance)
                  }
                  payload={
                    donutAdapted?.payload ?? {
                      chart: {
                        visualisation: "donut",
                        status: { on_track: 0, at_risk: 0, delayed: 0, total: 0 },
                      },
                    }
                  }
                  state="ready"
                />
              )}
            </Card>
          </div>
        )}
      </div>

      {mapOrder.length > 0 && (
        <div className={dashboardRowGrid}>
          <div style={{ minWidth: 0 }} data-dashboard-row-cell>
            <Row gutter={[16, 16]}>
              {mapOrder.map((slug) => (
                <BoardWidgetCard
                  key={slug}
                  slug={slug}
                  adapted={boardAdapted(slug)}
                  currency={currency}
                  isEditing={layout.isEditing}
                  refreshing={isRefreshing(slug)}
                  onHide={handleHide}
                  onRefresh={() => handleWidgetRefresh(slug)}
                  onExpand={() => setExpandedSlug(slug)}
                />
              ))}
            </Row>
          </div>
        </div>
      )}

      {boardChartRows(boardChartOrder).map((row) => {
        const cards = row.slugs.map((slug) => (
          <BoardWidgetCard
            key={slug}
            slug={slug}
            adapted={boardAdapted(slug)}
            currency={currency}
            isEditing={layout.isEditing}
            refreshing={isRefreshing(slug)}
            onHide={handleHide}
            onRefresh={() => handleWidgetRefresh(slug)}
            onExpand={() => setExpandedSlug(slug)}
            onEngagementDrill={engagementHandlers.onEngagementDrill}
            loadEngagementRange={engagementHandlers.loadEngagementRange}
          />
        ))
        return row.wide ? (
          <div key={row.slugs[0]} className="grid grid-cols-1" style={{ minWidth: 0 }}>
            {cards}
          </div>
        ) : (
          <div key={row.slugs[0]} className={dashboardRowGrid}>
            <div style={{ minWidth: 0 }} className="flex flex-col gap-4" data-dashboard-row-cell>
              {cards}
            </div>
          </div>
        )
      })}
      {engagementHost.drillModal}

      <AddTileDrawer
        open={layout.isEditing && addTileOpen}
        catalogTiles={catalogEntries}
        onAdd={handleAdd}
        onClose={() => setAddTileOpen(false)}
      />

      {boardListOrder.length > 0 && (
        <Row gutter={[16, 16]}>
          {boardListOrder.map((slug) => (
            <BoardWidgetCard
              key={slug}
              slug={slug}
              adapted={boardAdapted(slug)}
              currency={currency}
              isEditing={layout.isEditing}
              refreshing={isRefreshing(slug)}
              onHide={handleHide}
              onRefresh={() => handleWidgetRefresh(slug)}
              onExpand={() => setExpandedSlug(slug)}
            />
          ))}
        </Row>
      )}

      {feedAlertOrder.length > 0 && (
        <Row gutter={[16, 16]}>
          {feedAlertOrder.map((slug) => (
            <BoardWidgetCard
              key={slug}
              slug={slug}
              adapted={boardAdapted(slug)}
              currency={currency}
              isEditing={layout.isEditing}
              refreshing={isRefreshing(slug)}
              onHide={handleHide}
              onRefresh={() => handleWidgetRefresh(slug)}
              onExpand={() => setExpandedSlug(slug)}
            />
          ))}
        </Row>
      )}

      {nbaShown && (nba.length > 0 || layout.isEditing) && (
        <div>
          {layout.isEditing && (
            <RemoveTileBar
              title={t("portfolio.nba.title", "Next Best Actions")}
              onRemove={() => handleHide(NBA_FEED_SLUG)}
            />
          )}
          {nba.length > 0 ? (
            <WidgetRenderer {...feedWidget(nba)} state="ready" />
          ) : (
            // An empty feed renders nothing; while editing it still needs a
            // card, or its Remove would sit above an empty space.
            <Card size="small" title={t("portfolio.nba.title", "Next Best Actions")}>
              <Typography.Text type="secondary">
                {t("portfolio.nba.emptyEditing")}
              </Typography.Text>
            </Card>
          )}
        </div>
      )}

      <WidgetExpandModal
        widget={expandedAdapted?.widget ?? null}
        payload={(expandedAdapted?.payload as WidgetPayload) ?? null}
        currency={currency}
        onClose={() => setExpandedSlug(null)}
      />
    </Flex>
  )
}
